<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Withdrawal Management</h3>
          <p class="text-muted">Manage teacher earning withdrawals</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="primary" @click="refreshWithdrawals">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-select v-model="filter.status" @change="filterWithdrawals">
            <b-form-select-option :value="null">All Status</b-form-select-option>
            <b-form-select-option value="pending">Pending</b-form-select-option>
            <b-form-select-option value="approved">Approved</b-form-select-option>
            <b-form-select-option value="rejected">Rejected</b-form-select-option>
            <b-form-select-option value="completed">Completed</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="6">
          <b-form-input
            v-model="filter.search"
            placeholder="Search by teacher name or withdrawal ID"
            @input="searchWithdrawals"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="3">
          <b-card bg-variant="warning" text-variant="white">
            <h3 class="mb-0">{{ stats.pending || 0 }}</h3>
            <p class="mb-0">Pending Approvals</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="info" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.pendingAmount) }}</h3>
            <p class="mb-0">Pending Amount</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="success" text-variant="white">
            <h3 class="mb-0">{{ stats.approved || 0 }}</h3>
            <p class="mb-0">Approved</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="primary" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.totalPaid) }}</h3>
            <p class="mb-0">Total Paid Out (Net)</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="dark" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.platformFees) }}</h3>
            <p class="mb-0">Platform Fees (5%)</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Withdrawals Table -->
      <b-table
        :items="filteredWithdrawals"
        :fields="fields"
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
        striped
        hover
        responsive
        show-empty
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading withdrawals...</strong>
          </div>
        </template>

        <template #cell(withdrawal_id)="data">
          <code>{{ data.value }}</code>
        </template>

        <template #cell(teacher)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.teacher_name || 'N/A' }}</div>
            <small class="text-muted">ID: {{ data.item.user_id }}</small>
          </div>
        </template>

        <template #cell(amount)="data">
          <strong class="text-primary">PKR {{ formatMoney(data.value) }}</strong>
        </template>

        <template #cell(platform_fee)="data">
          PKR {{ formatMoney(data.value) }}
        </template>

        <template #cell(net_payout)="data">
          <strong class="text-success">PKR {{ formatMoney(data.value) }}</strong>
        </template>

        <template #cell(bank_details)="data">
          <div v-if="data.value">
            <div><strong>{{ data.value.bank_name }}</strong></div>
            <small>{{ data.value.account_number }}</small>
          </div>
          <span v-else class="text-muted">N/A</span>
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.value)">
            {{ getStatusLabel(data.value) }}
          </b-badge>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-button-group size="sm">
            <b-button
              v-if="data.item.status === 0"
              variant="success"
              @click="approveWithdrawal(data.item)"
            >
              <feather type="check" class="feather-sm"></feather>
              Approve
            </b-button>
            <b-button
              v-if="data.item.status === 0"
              variant="danger"
              @click="rejectWithdrawal(data.item)"
            >
              <feather type="x" class="feather-sm"></feather>
              Reject
            </b-button>
            <b-button variant="info" @click="viewWithdrawal(data.item)">
              <feather type="eye" class="feather-sm"></feather>
              View
            </b-button>
          </b-button-group>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col md="6">
          <span class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, filteredWithdrawals.length) }} of
            {{ filteredWithdrawals.length }} withdrawals
          </span>
        </b-col>
        <b-col md="6">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredWithdrawals.length"
            :per-page="perPage"
            align="right"
            size="sm"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- Withdrawal Detail Modal -->
    <b-modal
      v-model="showWithdrawalModal"
      size="lg"
      title="Withdrawal Details"
      hide-footer
    >
      <div v-if="selectedWithdrawal">
        <b-row>
          <b-col md="6">
            <h5>Teacher Information</h5>
            <p><strong>Name:</strong> {{ selectedWithdrawal.teacher_name }}</p>
            <p><strong>User ID:</strong> {{ selectedWithdrawal.user_id }}</p>
            <p><strong>Email:</strong> {{ selectedWithdrawal.teacher_email }}</p>
          </b-col>
          <b-col md="6">
            <h5>Withdrawal Information</h5>
            <p><strong>Withdrawal ID:</strong> <code>{{ selectedWithdrawal.withdrawal_id }}</code></p>
            <p><strong>Amount (Gross):</strong> <span class="text-primary font-weight-bold">PKR {{ formatMoney(selectedWithdrawal.amount) }}</span></p>
            <p><strong>Status:</strong> <b-badge :variant="getStatusVariant(selectedWithdrawal.status)">{{ getStatusLabel(selectedWithdrawal.status) }}</b-badge></p>
            <p><strong>Requested:</strong> {{ formatDate(selectedWithdrawal.created_at) }}</p>
          </b-col>
        </b-row>

        <hr />

        <h5>Fee Breakdown (5% platform fee)</h5>
        <b-row>
          <b-col md="4">
            <small class="text-muted">Gross Withdrawn</small>
            <p class="mb-0">PKR {{ formatMoney(selectedWithdrawal.amount) }}</p>
          </b-col>
          <b-col md="4">
            <small class="text-muted">Platform Fee</small>
            <p class="mb-0">PKR {{ formatMoney(selectedWithdrawal.platform_fee) }}</p>
          </b-col>
          <b-col md="4">
            <small class="text-muted">Net Payout</small>
            <p class="mb-0 font-weight-bold text-success">PKR {{ formatMoney(selectedWithdrawal.net_payout) }}</p>
          </b-col>
        </b-row>

        <hr />

        <h5>Bank Details</h5>
        <b-row v-if="selectedWithdrawal.bank_details">
          <b-col md="6">
            <p><strong>Bank Name:</strong> {{ selectedWithdrawal.bank_details.bank_name }}</p>
            <p><strong>Account Title:</strong> {{ selectedWithdrawal.bank_details.account_title }}</p>
          </b-col>
          <b-col md="6">
            <p><strong>Account Number:</strong> {{ selectedWithdrawal.bank_details.account_number }}</p>
            <p><strong>IBAN:</strong> {{ selectedWithdrawal.bank_details.iban || 'N/A' }}</p>
          </b-col>
        </b-row>

        <hr v-if="selectedWithdrawal.notes" />

        <div v-if="selectedWithdrawal.notes">
          <h5>Notes</h5>
          <p>{{ selectedWithdrawal.notes }}</p>
        </div>

        <div class="mt-4 text-right">
          <b-button
            v-if="selectedWithdrawal.status === 0"
            variant="success"
            @click="approveWithdrawal(selectedWithdrawal)"
            class="mr-2"
          >
            <feather type="check-circle" class="feather-sm mr-2"></feather>
            Approve
          </b-button>
          <b-button
            v-if="selectedWithdrawal.status === 0"
            variant="danger"
            @click="rejectWithdrawal(selectedWithdrawal)"
            class="mr-2"
          >
            <feather type="x-circle" class="feather-sm mr-2"></feather>
            Reject
          </b-button>
          <b-button variant="secondary" @click="showWithdrawalModal = false">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import WithdrawalService from '@/services/withdrawal.service';
import moment from 'moment';

export default {
  name: 'WithdrawalManagement',
  data() {
    return {
      withdrawals: [],
      filteredWithdrawals: [],
      loading: false,
      filter: {
        status: null,
        search: '',
      },
      stats: {
        pending: 0,
        pendingAmount: 0,
        approved: 0,
        totalPaid: 0,
        platformFees: 0,
      },
      fields: [
        { key: 'withdrawal_id', label: 'ID', sortable: true },
        { key: 'teacher', label: 'Teacher', sortable: true },
        { key: 'amount', label: 'Amount (Gross)', sortable: true },
        { key: 'platform_fee', label: 'Platform Fee (5%)', sortable: true },
        { key: 'net_payout', label: 'Net Payout', sortable: true },
        { key: 'bank_details', label: 'Bank Details' },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'created_at', label: 'Requested', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      perPage: 10,
      currentPage: 1,
      showWithdrawalModal: false,
      selectedWithdrawal: null,
    };
  },
  created() {
    this.loadWithdrawals();
  },
  methods: {
    async loadWithdrawals() {
      this.loading = true;
      try {
        const response = await WithdrawalService.getAllWithdrawals();
        this.withdrawals = response.data;
        this.filteredWithdrawals = this.withdrawals;
        this.calculateStats();
      } catch (error) {
        this.$bvToast.toast('Failed to load withdrawals', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    calculateStats() {
      // status is numeric: 0 = pending, 1 = processed/approved, 2 = rejected
      // (v1 has no separate admin-approval step, so every withdrawal is
      // processed immediately -- these codes only matter for the rare/legacy
      // pending row). These used to compare against string values that never
      // matched real rows, so "pending" silently lumped in rejected ones too.
      const pending = this.withdrawals.filter(w => w.status === 0);
      const processed = this.withdrawals.filter(w => w.status === 1);
      this.stats.pending = pending.length;
      this.stats.pendingAmount = pending.reduce((sum, w) => sum + (Number(w.amount) || 0), 0);
      this.stats.approved = processed.length;
      // Net Payout = what teachers actually received, after the platform fee.
      this.stats.totalPaid = processed.reduce((sum, w) => sum + (Number(w.net_payout ?? w.amount) || 0), 0);
      this.stats.platformFees = processed.reduce((sum, w) => sum + (Number(w.platform_fee) || 0), 0);
    },
    filterWithdrawals() {
      let result = [...this.withdrawals];

      if (this.filter.status) {
        const statusMap = { pending: 0, approved: 1, completed: 1, rejected: 2 };
        result = result.filter(w => w.status === statusMap[this.filter.status]);
      }

      if (this.filter.search) {
        const search = this.filter.search.toLowerCase();
        result = result.filter(
          w =>
            w.teacher_name?.toLowerCase().includes(search) ||
            // withdrawal_id is numeric (the real PK is with_id) -- String()
            // it before calling toLowerCase, which numbers don't have.
            String(w.withdrawal_id || '').toLowerCase().includes(search)
        );
      }

      this.filteredWithdrawals = result;
      this.currentPage = 1;
    },
    searchWithdrawals() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.filterWithdrawals();
      }, 300);
    },
    refreshWithdrawals() {
      this.loadWithdrawals();
    },
    viewWithdrawal(withdrawal) {
      this.selectedWithdrawal = withdrawal;
      this.showWithdrawalModal = true;
    },
    async approveWithdrawal(withdrawal) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to approve withdrawal of PKR ${this.formatMoney(withdrawal.amount)} for ${withdrawal.teacher_name}?`,
        {
          title: 'Confirm Approval',
          okVariant: 'success',
          okTitle: 'Approve',
          cancelTitle: 'Cancel',
        }
      );

      if (confirmed) {
        try {
          // The real PK is with_id (withdrawal_id is just an alias the
          // list endpoint adds) -- withdrawal.id doesn't exist on the row.
          await WithdrawalService.approveWithdrawal(withdrawal.with_id, {
            approved_at: new Date().toISOString(),
          });
          this.$bvToast.toast('Withdrawal approved successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
          this.showWithdrawalModal = false;
          this.loadWithdrawals();
        } catch (error) {
          this.$bvToast.toast('Failed to approve withdrawal', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          });
        }
      }
    },
    async rejectWithdrawal(withdrawal) {
      this.$bvModal
        .msgBoxConfirm('Please provide a reason for rejecting this withdrawal:', {
          title: 'Reject Withdrawal',
          okVariant: 'danger',
          okTitle: 'Reject',
          cancelTitle: 'Cancel',
        })
        .then(async (confirmed) => {
          if (confirmed) {
            try {
              await WithdrawalService.rejectWithdrawal(withdrawal.with_id, 'Admin rejected');
              this.$bvToast.toast('Withdrawal rejected', {
                title: 'Success',
                variant: 'success',
                solid: true,
              });
              this.showWithdrawalModal = false;
              this.loadWithdrawals();
            } catch (error) {
              this.$bvToast.toast('Failed to reject withdrawal', {
                title: 'Error',
                variant: 'danger',
                solid: true,
              });
            }
          }
        });
    },
    getStatusLabel(status) {
      return status === 1 ? 'Processed' : (status === 2 ? 'Rejected' : 'Pending');
    },
    getStatusVariant(status) {
      if (status === 1) return 'success';
      if (status === 2) return 'danger';
      return 'warning';
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY HH:mm');
    },
    formatMoney(amount) {
      return Number(amount || 0).toLocaleString();
    },
  },
};
</script>

<style scoped>
.feather-sm {
  width: 16px;
  height: 16px;
}
</style>
